/**
 * `GanttInspector` — the right-docked metadata panel for a selected gantt task/milestone.
 *
 * Opens when a bar is clicked on `GanttCanvas`. Edits the structured fields that serialize to the
 * mermaid task line (title, start, length, status, owner, milestone) plus the two that have no
 * mermaid home and ride in the `%% @aletheia:gantt` sidecar (a free-text **description** and
 * arbitrary **key/value fields**) — the same source of truth MCP and the chat interface write, so
 * a description added here is visible to a model and vice-versa.
 *
 * Each control commits through `onPatch(patch)`; the parent applies it via `updateTask` (which keeps
 * the mermaid invariants — start XOR after, duration XOR end, milestone ⇒ zero length) and
 * re-serializes to the fence. Text inputs commit on blur/Enter; selects and the milestone toggle
 * commit immediately. Local draft state is re-seeded whenever the selected task id changes.
 */

import { useEffect, useMemo, useState } from 'react';
import type { GanttStatus, GanttTask } from '../gantt';
import type { GanttTaskPatch } from '../ganttLayout';

const STATUS_OPTIONS: { value: GanttStatus; label: string }[] = [
  { value: 'normal', label: 'Planned' },
  { value: 'active', label: 'In progress' },
  { value: 'done', label: 'Complete' },
  { value: 'crit', label: 'Critical path' },
];

export interface GanttInspectorProps {
  task: GanttTask;
  onPatch: (patch: GanttTaskPatch) => void;
  onClose: () => void;
}

/** [key, value] pairs, editable in order (a plain object loses ordering + can't hold a blank key). */
type Pair = [string, string];

function toPairs(fields: Record<string, string>): Pair[] {
  return Object.entries(fields);
}

function fromPairs(pairs: Pair[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of pairs) {
    const key = k.trim();
    if (key) out[key] = v;
  }
  return out;
}

export function GanttInspector({ task, onPatch, onClose }: GanttInspectorProps): JSX.Element {
  // Draft copies for the free-text controls, re-seeded when the selected task changes (by id) so
  // switching selection loads the new task's values without clobbering an in-progress edit.
  const [title, setTitle] = useState(task.title);
  const [owner, setOwner] = useState(task.owner ?? '');
  const [start, setStart] = useState(task.start ?? '');
  const [duration, setDuration] = useState(task.duration ?? '');
  const [end, setEnd] = useState(task.end ?? '');
  const [description, setDescription] = useState(task.description ?? '');
  const [pairs, setPairs] = useState<Pair[]>(() => toPairs(task.fields));

  useEffect(() => {
    setTitle(task.title);
    setOwner(task.owner ?? '');
    setStart(task.start ?? '');
    setDuration(task.duration ?? '');
    setEnd(task.end ?? '');
    setDescription(task.description ?? '');
    setPairs(toPairs(task.fields));
    // Re-seed only on identity change — same task edited elsewhere keeps the user's in-flight text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  const isAfterAnchored = task.after.length > 0 && !task.start;
  const anchorNote = useMemo(
    () => (isAfterAnchored ? `Starts after: ${task.after.join(', ')}` : null),
    [isAfterAnchored, task.after],
  );

  const commitFields = (next: Pair[]): void => {
    setPairs(next);
    onPatch({ fields: fromPairs(next) });
  };

  return (
    <aside
      className="gantt-inspector"
      aria-label="Task details"
      data-testid="gantt-inspector"
      onKeyDown={(e) => {
        // Esc closes the inspector, not the fullscreen overlay behind it.
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="gantt-inspector__head">
        <span className="gantt-inspector__eyebrow">{task.milestone ? 'Milestone' : 'Task'}</span>
        <button
          type="button"
          className="gantt-inspector__close"
          title="Close (Esc)"
          onClick={onClose}
          data-testid="gantt-inspector-close"
        >
          ✕
        </button>
      </div>

      <div className="gantt-inspector__body">
        <label className="gantt-field">
          <span className="gantt-field__label">Title</span>
          <input
            className="gantt-field__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title !== task.title && onPatch({ title })}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            data-testid="gantt-inspector-title"
          />
        </label>

        <label className="gantt-field gantt-field--check">
          <input
            type="checkbox"
            checked={task.milestone}
            onChange={(e) => onPatch({ milestone: e.target.checked })}
          />
          <span className="gantt-field__label">Milestone (a point in time)</span>
        </label>

        {!task.milestone && (
          <label className="gantt-field">
            <span className="gantt-field__label">Status</span>
            <select
              className="gantt-field__input"
              value={task.status}
              onChange={(e) => onPatch({ status: e.target.value as GanttStatus })}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="gantt-field">
          <span className="gantt-field__label">Start date</span>
          <input
            type="date"
            className="gantt-field__input"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            onBlur={() => {
              const v = start.trim();
              if (v && v !== task.start) onPatch({ start: v });
            }}
          />
          {anchorNote && <span className="gantt-field__hint">{anchorNote} — set a date to pin it.</span>}
        </label>

        {!task.milestone && (
          <div className="gantt-field-row">
            <label className="gantt-field">
              <span className="gantt-field__label">Duration</span>
              <input
                className="gantt-field__input"
                placeholder="e.g. 10d, 2w"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                onBlur={() => {
                  const v = duration.trim();
                  if (v && v !== task.duration) onPatch({ duration: v });
                }}
              />
            </label>
            <label className="gantt-field">
              <span className="gantt-field__label">or End date</span>
              <input
                type="date"
                className="gantt-field__input"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                onBlur={() => {
                  const v = end.trim();
                  if (v && v !== task.end) onPatch({ end: v });
                }}
              />
            </label>
          </div>
        )}

        <label className="gantt-field">
          <span className="gantt-field__label">Owner</span>
          <input
            className="gantt-field__input"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            onBlur={() => (owner.trim() || null) !== task.owner && onPatch({ owner: owner.trim() || null })}
          />
        </label>

        <label className="gantt-field">
          <span className="gantt-field__label">Description</span>
          <textarea
            className="gantt-field__input gantt-field__textarea"
            rows={4}
            placeholder="Notes, context, acceptance criteria…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() =>
              (description.trim() || null) !== (task.description || null) &&
              onPatch({ description: description.trim() || null })
            }
            onKeyDown={(e) => e.stopPropagation()}
            data-testid="gantt-inspector-description"
          />
        </label>

        <div className="gantt-field">
          <span className="gantt-field__label">Custom fields</span>
          <div className="gantt-kv">
            {pairs.map(([k, v], i) => (
              <div className="gantt-kv__row" key={i}>
                <input
                  className="gantt-kv__key"
                  placeholder="key"
                  value={k}
                  onChange={(e) => setPairs(pairs.map((p, j) => (j === i ? [e.target.value, p[1]] : p)))}
                  onBlur={() => commitFields(pairs)}
                />
                <input
                  className="gantt-kv__val"
                  placeholder="value"
                  value={v}
                  onChange={(e) => setPairs(pairs.map((p, j) => (j === i ? [p[0], e.target.value] : p)))}
                  onBlur={() => commitFields(pairs)}
                />
                <button
                  type="button"
                  className="gantt-kv__del"
                  title="Remove field"
                  onClick={() => commitFields(pairs.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="gantt-kv__add"
              onClick={() => setPairs([...pairs, ['', '']])}
              data-testid="gantt-inspector-add-field"
            >
              + Add field
            </button>
          </div>
        </div>

        <p className="gantt-inspector__foot">
          Description and custom fields are shared with MCP and chat via the chart’s sidecar.
        </p>
      </div>
    </aside>
  );
}
